import React, { useMemo } from "react";

import { faThumbTack, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFiles } from "@fortawesome/sharp-solid-svg-icons/faFiles";
import PopConfirm from "antd/es/popconfirm";
import { ColumnType } from "antd/es/table";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { IntlShape } from "react-intl";

import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import { UserRef } from "@mapmycustomers/shared/types/User";
import { AvatarWithName, Button } from "@mapmycustomers/ui";

import UserPreviewCard from "@app/component/UserPreviewCard";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { MAX_PINNED_DASHBOARDS } from "@app/util/consts";
import { formatRawDate } from "@app/util/formatters";

import styles from "./useCumulColumns.module.scss";

const useCumulColumns = (
  hasEditAccess: boolean,
  intl: IntlShape,
  onClickOpenButton: (id: CumulDashboard["id"]) => void,
  onDeleteDashboard: (id: CumulDashboard["id"]) => void,
  onDuplicateDashboard: (id: CumulDashboard["id"]) => void,
  onTogglePinnedDashboard: (id: CumulDashboard["id"]) => void,
  pinnedDashboardIds: CumulDashboard["id"][],
  analytics: AnalyticsService
): ColumnType<CumulDashboard>[] =>
  useMemo(
    () => [
      {
        dataIndex: "",
        key: "",
        render: (dashboard: CumulDashboard) => (
          <div className={styles.nameCell}>
            <div className={styles.name}>{dashboard.name?.en ?? ""}</div>
            <Button
              className={styles.openButton}
              onClick={() => onClickOpenButton(dashboard.id)}
              type="default"
            >
              {intl.formatMessage({
                id: "reports.cumul.dashboard.open",
                defaultMessage: "Open",
                description: "Title of the Open button in the Name column",
              })}
            </Button>
          </div>
        ),
        title: intl.formatMessage({
          id: "reports.cumul.dashboard.list.name",
          defaultMessage: "Report Name",
          description: "Name of a report column in cumul dashbaords table on reports page",
        }),
      },
      {
        dataIndex: "modified_at",
        key: "modified_at",
        render: (modifiedAt: string) => {
          return <div>{formatRawDate(modifiedAt, "PPp")}</div>;
        },
        title: intl.formatMessage({
          id: "reports.cumul.dashboard.list.modifiedAt",
          defaultMessage: "Last Update",
          description: "Created At column in cumul dashbaords table on reports page",
        }),
      },
      {
        dataIndex: "createdBy",
        key: "createdBy",
        render: (createdBy: UserRef) =>
          createdBy && (
            <UserPreviewCard userId={createdBy.id}>
              <div>
                <AvatarWithName actionOnHover user={createdBy} />
              </div>
            </UserPreviewCard>
          ),
        title: intl.formatMessage({
          id: "reports.cumul.dashboard.list.createdBy",
          defaultMessage: "Created By",
          description: "Created At column in cumul dashbaords table on reports page",
        }),
      },
      {
        dataIndex: "actions",
        key: "actions",
        render: (_: any, dashboard: CumulDashboard) => {
          const isDuplicateAllowed = dashboard.userAccessRight
            ? dashboard.userAccessRight.flagUse ||
              dashboard.userAccessRight.flagModify ||
              dashboard.userAccessRight.flagOwn
            : false;
          const isDeleteDashboardAllowed = dashboard.userAccessRight
            ? dashboard.userAccessRight.flagOwn
            : false;

          return (
            <div className={styles.actions}>
              <div>
                <span className={styles.duplicateAction}>
                  <Tooltip
                    placement="top"
                    title={intl.formatMessage({
                      id: "reports.cumul.dashboardList.action.duplicate.tooltip",
                      defaultMessage: "Duplicate",
                      description: "Duplicate dashboard tooltip on the dahsboard list",
                    })}
                  >
                    <Button
                      disabled={!hasEditAccess || !isDuplicateAllowed}
                      icon={
                        <FontAwesomeIcon
                          className={cn(styles.actionIcons, {
                            [styles.disabledActions]: !hasEditAccess || !isDuplicateAllowed,
                          })}
                          icon={faFiles}
                        />
                      }
                      iconOnly
                      onClick={() => onDuplicateDashboard(dashboard.id)}
                      type="link"
                    />
                  </Tooltip>
                </span>
                <PopConfirm
                  cancelText={intl.formatMessage({
                    id: "reports.cumul.dashboardList.action.delete.wait",
                    defaultMessage: "Wait",
                    description: "Delete dashboard Cancel button on the list",
                  })}
                  disabled={!hasEditAccess || !isDeleteDashboardAllowed}
                  okText={intl.formatMessage({
                    id: "reports.cumul.dashboardList.action.delete.yes",
                    defaultMessage: "Yes, Delete",
                    description: "Delete dashboard Confirm button on the list",
                  })}
                  onConfirm={() => {
                    analytics.clicked(["Delete"], { id: dashboard.id, name: dashboard.name.en });
                    onDeleteDashboard(dashboard.id);
                  }}
                  overlayClassName={styles.popover}
                  placement="rightTop"
                  title={intl.formatMessage({
                    id: "reports.cumul.dashboardList.action.delete.confirm",
                    defaultMessage:
                      "Are you sure to delete this Dashboard for all users? This action is permanent.",
                    description: "Delete dashboard confirm on the dahsboard list",
                  })}
                >
                  <Tooltip
                    placement="top"
                    title={intl.formatMessage({
                      id: "reports.cumul.dashboardList.action.delete.tooltip",
                      defaultMessage: "Delete",
                      description: "Delete dashboard tooltip on the dahsboard list",
                    })}
                  >
                    <Button
                      disabled={!hasEditAccess || !isDeleteDashboardAllowed}
                      icon={
                        <FontAwesomeIcon
                          className={cn(styles.actionIcons, {
                            [styles.disabledActions]: !hasEditAccess || !isDeleteDashboardAllowed,
                          })}
                          icon={faTrash}
                        />
                      }
                      iconOnly
                      type="link"
                    />
                  </Tooltip>
                </PopConfirm>
              </div>
              <Tooltip
                placement="top"
                title={
                  pinnedDashboardIds.some((pinned) => pinned === dashboard.id)
                    ? intl.formatMessage({
                        id: "reports.cumul.dashboard.list.pinAction.tooltip.pinnedTitle",
                        defaultMessage: "Unpin from Menu",
                        description: "message for unpin a pinned dashboard tooltip",
                      })
                    : pinnedDashboardIds.length >= MAX_PINNED_DASHBOARDS
                    ? intl.formatMessage({
                        id: "reports.cumul.dashboard.list.pinAction.tooltip.disabledTitle",
                        defaultMessage: "Only 5 pinned dashboards permitted",
                        description:
                          "message for disabled pinned button for more than 5 pinned dashbaords tooltip",
                      })
                    : pinnedDashboardIds.length < MAX_PINNED_DASHBOARDS
                    ? intl.formatMessage({
                        id: "reports.cumul.dashboard.list.pinAction.tooltip.unpinnedTitle",
                        defaultMessage: "Pin to Menu",
                        description: "message for pin to menu button tooltip",
                      })
                    : undefined
                }
              >
                <Button
                  className={cn({
                    [styles.pinAction]:
                      pinnedDashboardIds.length < MAX_PINNED_DASHBOARDS &&
                      !pinnedDashboardIds.some((pinned) => pinned === dashboard.id),
                    [styles.pinButton]: !pinnedDashboardIds.some(
                      (pinned) => pinned === dashboard.id
                    ),
                  })}
                  disabled={
                    pinnedDashboardIds.length >= MAX_PINNED_DASHBOARDS &&
                    !pinnedDashboardIds.some((pinned) => pinned === dashboard.id)
                  }
                  icon={<FontAwesomeIcon icon={faThumbTack} />}
                  iconOnly
                  onClick={() => {
                    analytics.clicked(["Pin"], { id: dashboard.id, name: dashboard.name.en });
                    onTogglePinnedDashboard(dashboard.id);
                  }}
                  type="filled"
                />
              </Tooltip>
            </div>
          );
        },
        title: intl.formatMessage({
          id: "reports.cumul.dashboard.list.actions",
          defaultMessage: "Actions",
          description: "Actions column in cumul dashbaords table on reports page",
        }),
      },
    ],
    [
      hasEditAccess,
      intl,
      onClickOpenButton,
      onDeleteDashboard,
      onDuplicateDashboard,
      onTogglePinnedDashboard,
      pinnedDashboardIds,
      analytics,
    ]
  );

export default useCumulColumns;
